// src/theme/theme.js
import { createTheme, alpha } from '@mui/material/styles';

// Define color palette - Professional corporate colors
const colors = {
  primary: {
    main: '#1e3a8a', // Deep professional blue
    light: '#3b82f6',
    dark: '#1e40af',
    contrastText: '#ffffff',
  },
  secondary: {
    main: '#6366f1', // Indigo accent
    light: '#818cf8',
    dark: '#4f46e5',
    contrastText: '#ffffff',
  },
  success: {
    main: '#16a34a', // Green
    light: '#4ade80',
    dark: '#15803d',
  },
  error: {
    main: '#dc2626', // Red
    light: '#f87171',
    dark: '#b91c1c',
  },
  warning: {
    main: '#f59e0b', // Orange
    light: '#fbbf24',
    dark: '#d97706',
  },
  info: {
    main: '#0891b2', // Teal
    light: '#22d3ee',
    dark: '#0e7490',
  },
};

// Common theme options
const commonTheme = {
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h6: {
      fontWeight: 600,
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
          padding: '8px 16px',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          '& .MuiInputLabel-root:not(.Mui-focused):not(.Mui-error)': {
            color: 'inherit', // Will use theme.palette.text.secondary
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(0, 0, 0, 0.23)',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.MuiInputLabel-shrink:not(.Mui-focused):not(.Mui-error)': {
            color: 'inherit', // Will use theme.palette.text.secondary
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
        },
      },
    },
    /*
     * MUI Chip bug workaround. Chip's root slot wires up
     *   onClick: event => { handlers.onClick?.(event); onClick(event); }
     * with NO guard on the second call, so clicking a Chip that was given no handler throws
     * "onClick is not a function" and takes the tab down.
     *
     * The chips that hit this are the Granted/Missing labels inside core's permissions drawer,
     * which cannot be patched from here -- the drawer ships compiled inside
     * @1823-partners/core. MUI applies defaultProps to every instance of a component, including
     * ones rendered from another bundle, so supplying the no-op here reaches them.
     *
     * It lives in this file rather than as a themeOptions prop because this app builds its own
     * theme (see ThemeContext) instead of using core's DarkModeProvider, which is where the
     * other apps hang it.
     *
     * clickable:false is the other half and is not redundant: MUI computes
     *   clickable = clickableProp !== false && onClick ? true : clickableProp
     * so the no-op alone would promote every chip in the app to a ButtonBase with a ripple and a
     * pointer cursor. This app has no genuinely clickable chip; one added later must pass
     * clickable explicitly.
     */
    MuiChip: {
      defaultProps: { clickable: false, onClick: () => {} },
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
  },
};

// Light theme
export const lightTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'light',
    ...colors,
    background: {
      default: '#f8fafc',
      paper: '#ffffff',
    },
    text: {
      primary: '#1e293b',
      secondary: '#64748b',
    },
    divider: '#e2e8f0',
  },
});

// Dark theme
export const darkTheme = createTheme({
  ...commonTheme,
  palette: {
    mode: 'dark',
    ...colors,
    background: {
      default: '#0f172a',
      paper: '#1e293b',
    },
    text: {
      primary: '#f1f5f9',
      secondary: '#cbd5e1',
    },
    divider: '#334155',
  },
  components: {
    ...commonTheme.components,
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#1e293b',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.23) !important',
          },
          '&:hover:not(.Mui-focused):not(.Mui-error) .MuiOutlinedInput-notchedOutline': {
            borderColor: 'rgba(255, 255, 255, 0.4) !important',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: 2,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#cbd5e1',
          '&.MuiInputLabel-shrink': {
            color: 'rgba(255, 255, 255, 0.7) !important',
          },
          '&.Mui-focused:not(.Mui-error)': {
            color: 'rgba(255, 255, 255, 0.7) !important',
          },
          '&.Mui-error': {
            color: '#dc2626 !important',
          },
        },
      },
    },
  },
});

